import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '../contexts/OrganizationContext'
import { resourceApi } from '../services/api'

export function useResources() {
  const { currentOrg } = useOrganization()
  const queryClient = useQueryClient()
  const orgSlug = currentOrg?.slug

  const useResourcesList = (options = {}) => {
    return useQuery({
      queryKey: ['resources', orgSlug],
      queryFn: async () => {
        if (!orgSlug) throw new Error('No organization selected')
        const response = await resourceApi.list(orgSlug)
        // Backend returns { resources: [...], count: n }
        return response.resources || response || []
      },
      enabled: !!orgSlug,
      ...options
    })
  }

  const useResource = (resourceId, options = {}) => {
    return useQuery({
      queryKey: ['resource', orgSlug, resourceId],
      queryFn: () => resourceApi.get(orgSlug, resourceId),
      enabled: !!orgSlug && !!resourceId,
      ...options
    })
  }

  const useCreateResource = () => {
    return useMutation({
      mutationFn: (resource) => resourceApi.create(orgSlug, resource),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['resources', orgSlug] })
      }
    })
  }

  const useUpdateResource = () => {
    return useMutation({
      mutationFn: ({ id, updates }) => resourceApi.update(orgSlug, id, updates),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['resources', orgSlug] })
        queryClient.invalidateQueries({ queryKey: ['resource', orgSlug, variables.id] })
      }
    })
  }

  const useDeleteResource = () => {
    return useMutation({
      mutationFn: (resourceId) => resourceApi.delete(orgSlug, resourceId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['resources', orgSlug] })
      }
    })
  }

  const useTestResource = () => {
    return useMutation({
      mutationFn: (resourceId) => resourceApi.test(orgSlug, resourceId),
      onSuccess: (data, resourceId) => {
        queryClient.invalidateQueries({ queryKey: ['resource', orgSlug, resourceId] })
      }
    })
  }

  const useResourceMetrics = (resourceId, options = {}) => {
    return useQuery({
      queryKey: ['resource-metrics', orgSlug, resourceId],
      queryFn: () => resourceApi.getMetrics(orgSlug, resourceId),
      enabled: !!orgSlug && !!resourceId,
      ...options
    })
  }

  const useResourceQuery = (resourceId, query, options = {}) => {
    return useMutation({
      mutationFn: (queryData) => resourceApi.query(orgSlug, resourceId, queryData || query),
      ...options
    })
  }

  return {
    useResourcesList,
    useResource,
    useCreateResource,
    useUpdateResource,
    useDeleteResource,
    useTestResource,
    useResourceMetrics,
    useResourceQuery
  }
}